import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import BrandLockup from '../components/BrandLockup';
import { Button, ButtonLink } from '../components/ui/Button';
import { trackDeckExported, trackDeckSaved } from '../analytics';
import { updateDeck } from '../api/decks';
import {
  loadActiveDeckId,
  loadActiveDeckLogoUrl,
  loadActiveDeckName,
  saveActiveDeckLogoUrl,
  saveActiveDeckName,
} from '../data/deckStore';
import {
  ElementFrame,
  PitchSlide,
  SlideBackground,
  SlideComponentType,
  SlideImageStyle,
  SlideLayout,
} from '../data/slides';
import {
  addComponentToLayout,
  isComponentId,
  removeComponentFromLayout,
  updateComponentInLayout,
} from '../data/slideComponents';
import { isBuiltinLayerId, LayerId } from '../data/layerOrder';
import { useLayerLabel } from '../i18n/useLayerLabel';
import {
  createBlankSlide,
  exportSlides,
  getDefaultSlide,
  importSlides,
  loadSlides,
  resetSlides,
  saveSlides,
} from '../data/slidesStore';
import { createDefaultLayout } from '../data/defaultLayout';
import { appTheme, pitchTheme } from '../styles/theme';
import SlideCanvas from '../components/editor/SlideCanvas';
import ComponentsPanel from '../components/editor/ComponentsPanel';
import ElementInspector from '../components/editor/ElementInspector';
import LayersPanel from '../components/editor/LayersPanel';
import AiSlidePanel from '../components/editor/AiSlidePanel';
import SidebarAccordion, { SidebarAccordionSection } from '../components/editor/SidebarAccordion';
import SlideSidebar from '../components/editor/SlideSidebar';
import { useAuth } from '../context/AuthContext';

const Page = styled.main`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${appTheme.pageBg};
`;

const TopBar = styled.header`
  position: sticky;
  top: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 24px;
  border-bottom: 1px solid ${appTheme.border};
  background: rgba(250, 250, 248, 0.94);
  backdrop-filter: blur(12px);
`;

const BrandLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  margin-right: 8px;
  flex-shrink: 0;

  &:focus-visible {
    outline: 2px solid ${appTheme.green};
    outline-offset: 2px;
    border-radius: ${appTheme.radiusSm};
  }
`;

const BackToDecksLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 8px 12px;
  border: 1px solid ${appTheme.border};
  border-radius: ${appTheme.radiusPill};
  background: ${appTheme.surface};
  color: ${appTheme.text};
  font-family: ${pitchTheme.fontUi};
  font-size: 0.8125rem;
  font-weight: 600;
  text-decoration: none;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: ${appTheme.green};
    background: ${appTheme.mint};
  }

  &:focus-visible {
    outline: 2px solid ${appTheme.green};
    outline-offset: 2px;
  }
`;

const ToolbarDivider = styled.span`
  width: 1px;
  height: 28px;
  background: ${appTheme.border};
  flex-shrink: 0;
`;

const DeckTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  max-width: min(360px, 100%);
`;

const DeckName = styled.span<{ $editing?: boolean }>`
  font-family: ${pitchTheme.fontPageTitle};
  font-size: 1rem;
  font-weight: 500;
  color: ${appTheme.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border-radius: ${appTheme.radiusSm};
  padding: 2px 4px;

  ${({ $editing }) =>
    $editing &&
    `
    outline: 2px solid ${appTheme.green};
    outline-offset: 1px;
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
  `}
`;

const EditNameButton = styled.button`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border: 1px solid ${appTheme.border};
  border-radius: ${appTheme.radiusMd};
  background: ${appTheme.surface};
  color: ${appTheme.textMuted};
  font-size: 0.875rem;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: ${appTheme.surfaceHover};
    color: ${appTheme.green};
    border-color: ${appTheme.green};
  }

  &:focus-visible {
    outline: 2px solid ${appTheme.green};
    outline-offset: 2px;
  }
`;

const NameSaveHint = styled.span`
  font-size: 0.6875rem;
  font-weight: 600;
  color: ${appTheme.textMuted};
  white-space: nowrap;
`;

const ToolbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-left: auto;
`;

const DirtyBadge = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${appTheme.orange};
  background: #fff4e6;
  border-radius: ${appTheme.radiusPill};
  padding: 4px 10px;
  white-space: nowrap;
`;

const EditorBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 24px 32px;
`;

const EditorGrid = styled.div`
  flex: 1;
  display: grid;
  gap: 16px;
  grid-template-columns: 250px 180px 1fr minmax(280px, 500px);
  align-items: start;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const CanvasColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const CanvasHint = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: ${appTheme.textMuted};
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const seedLayout = (slide: PitchSlide, index: number): SlideLayout =>
  slide.layout ?? createDefaultLayout(index % 2 === 0);

const EditorPage: React.FC = () => {
  const { t } = useTranslation();
  const layerLabel = useLayerLabel();
  const { user } = useAuth();
  const [slides, setSlides] = useState<PitchSlide[]>(() => loadSlides());
  const [deckId] = useState(() => loadActiveDeckId());
  const [deckName, setDeckName] = useState(() => loadActiveDeckName());
  const [logoUrl, setLogoUrl] = useState<string | null>(() => loadActiveDeckLogoUrl());
  const [isEditingName, setIsEditingName] = useState(false);
  const [isSavingName, setIsSavingName] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedLayerId, setSelectedLayerId] = useState<LayerId | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLSpanElement>(null);
  const nameBeforeEditRef = useRef(deckName);

  const slide = slides[selectedIndex];
  const layout = useMemo(() => seedLayout(slide, selectedIndex), [slide, selectedIndex]);

  useEffect(() => {
    if (!isEditingName || !nameRef.current) {
      return;
    }
    nameRef.current.textContent = deckName;
    nameRef.current.focus();
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(nameRef.current);
    selection?.removeAllRanges();
    selection?.addRange(range);
  }, [isEditingName]);

  const startEditingName = () => {
    nameBeforeEditRef.current = deckName;
    setIsEditingName(true);
  };

  const cancelEditingName = () => {
    setDeckName(nameBeforeEditRef.current);
    if (nameRef.current) {
      nameRef.current.textContent = nameBeforeEditRef.current;
    }
    setIsEditingName(false);
  };

  const saveDeckName = async () => {
    if (!isEditingName || isSavingName) {
      return;
    }

    const nextName = (nameRef.current?.textContent ?? deckName).trim();
    if (!nextName) {
      cancelEditingName();
      return;
    }

    if (nextName === deckName) {
      setIsEditingName(false);
      return;
    }

    setIsSavingName(true);
    try {
      if (deckId) {
        await updateDeck(deckId, { name: nextName });
      }
      setDeckName(nextName);
      saveActiveDeckName(nextName);
      setIsEditingName(false);
    } catch (error) {
      window.alert(error instanceof Error ? error.message : t('editor.saveNameError'));
      cancelEditingName();
    } finally {
      setIsSavingName(false);
    }
  };

  const handleNameKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void saveDeckName();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      cancelEditingName();
    }
  };

  const updateSlide = useCallback(
    (changes: Partial<PitchSlide>) => {
      setSlides((current) =>
        current.map((item, index) =>
          index === selectedIndex ? { ...item, ...changes } : item
        )
      );
      setIsDirty(true);
    },
    [selectedIndex]
  );

  const updateLayout = useCallback(
    (changes: Partial<SlideLayout>) => {
      updateSlide({ layout: { ...layout, ...changes } });
    },
    [layout, updateSlide]
  );

  const handleLayerFrameChange = useCallback(
    (layerId: LayerId, frame: ElementFrame) => {
      if (isBuiltinLayerId(layerId)) {
        updateLayout({ [layerId]: frame });
        return;
      }
      if (isComponentId(layerId)) {
        updateLayout(updateComponentInLayout(layout, layerId, { frame }));
      }
    },
    [layout, updateLayout]
  );

  const handleLayerOrderChange = useCallback(
    (layerOrder: LayerId[]) => {
      updateLayout({ layerOrder });
    },
    [updateLayout]
  );

  const handleAddComponent = useCallback(
    (type: SlideComponentType) => {
      const { layout: nextLayout, component } = addComponentToLayout(layout, type);
      updateSlide({ layout: nextLayout });
      setSelectedLayerId(component.id);
    },
    [layout, updateSlide]
  );

  const handleComponentChange = useCallback(
    (componentId: string, changes: Parameters<typeof updateComponentInLayout>[2]) => {
      updateLayout(updateComponentInLayout(layout, componentId, changes));
    },
    [layout, updateLayout]
  );

  const handleComponentRemove = useCallback(
    (componentId: string) => {
      updateLayout(removeComponentFromLayout(layout, componentId));
      setSelectedLayerId((current) => (current === componentId ? null : current));
    },
    [layout, updateLayout]
  );

  const handleBackgroundChange = useCallback(
    (background: SlideBackground) => {
      updateLayout({ background });
    },
    [updateLayout]
  );

  const handleImageStyleChange = useCallback(
    (imageStyle: SlideImageStyle) => {
      updateLayout({ imageStyle });
    },
    [updateLayout]
  );

  const handleSave = async () => {
    saveSlides(slides);
    saveActiveDeckLogoUrl(logoUrl);
    setIsDirty(false);
    trackDeckSaved(slides.length);

    if (deckId) {
      try {
        await updateDeck(deckId, { slides, logoUrl });
      } catch (error) {
        window.alert(error instanceof Error ? error.message : t('editor.saveDeckError'));
        setIsDirty(true);
      }
    }
  };

  const handleLogoChange = (nextLogoUrl: string | null) => {
    setLogoUrl(nextLogoUrl);
    setIsDirty(true);
  };

  const handleResetSlideLayout = () => {
    const defaultSlide = getDefaultSlide(slide.id);
    setSlides((current) =>
      current.map((item, index) =>
        index === selectedIndex
          ? { ...item, layout: defaultSlide?.layout }
          : item
      )
    );
    setIsDirty(true);
  };

  const handleResetAll = () => {
    if (!window.confirm(t('editor.resetAllConfirm'))) {
      return;
    }
    const defaults = resetSlides();
    setSlides(defaults);
    setSelectedIndex(0);
    setSelectedLayerId(null);
    setIsDirty(false);
  };

  const handleExport = () => {
    const blob = new Blob([exportSlides(slides)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'spotdove-slides.json';
    anchor.click();
    URL.revokeObjectURL(url);
    trackDeckExported(slides.length);
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    try {
      const imported = importSlides(await file.text());
      setSlides(imported);
      setSelectedIndex(0);
      setSelectedLayerId(null);
      setIsDirty(false);
    } catch (error) {
      window.alert(error instanceof Error ? error.message : t('editor.importError'));
    }
  };

  const selectSlide = (index: number) => {
    setSelectedIndex(index);
    setSelectedLayerId(null);
  };

  const handleAddSlide = () => {
    const nextIndex = slides.length;
    setSlides((current) => [...current, createBlankSlide(current)]);
    setSelectedIndex(nextIndex);
    setSelectedLayerId(null);
    setIsDirty(true);
  };

  const handleReorderSlides = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) {
      return;
    }

    setSlides((current) => {
      const next = [...current];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });

    setSelectedIndex((current) => {
      if (current === fromIndex) {
        return toIndex;
      }
      if (fromIndex < current && toIndex >= current) {
        return current - 1;
      }
      if (fromIndex > current && toIndex <= current) {
        return current + 1;
      }
      return current;
    });

    setIsDirty(true);
  };

  const handleDeckAiChange = (nextSlides: PitchSlide[], nextName?: string) => {
    setSlides(nextSlides);
    if (nextName) {
      setDeckName(nextName);
      saveActiveDeckName(nextName);
    }
    setSelectedLayerId(null);
    setIsDirty(true);
  };

  return (
    <Page>
      <TopBar>
        <BackToDecksLink to="/decks">{t('editor.backToDecks')}</BackToDecksLink>
        <ToolbarDivider aria-hidden />
        <BrandLink to="/decks" aria-label={t('editor.myDecks')}>
          <BrandLockup logoHeight={28} />
        </BrandLink>
        <ToolbarDivider aria-hidden />
        <DeckTitleGroup>
          {isEditingName ? (
            <DeckName
              ref={nameRef}
              $editing
              contentEditable
              suppressContentEditableWarning
              role="textbox"
              aria-label={t('editor.deckNameAria')}
              onBlur={() => {
                void saveDeckName();
              }}
              onKeyDown={handleNameKeyDown}
            />
          ) : (
            <>
              <DeckName>{deckName}</DeckName>
              <EditNameButton
                type="button"
                aria-label={t('editor.editDeckNameAria')}
                disabled={isSavingName}
                onClick={startEditingName}
              >
                ✎
              </EditNameButton>
            </>
          )}
          {isEditingName && <NameSaveHint>{t('editor.nameSaveHint')}</NameSaveHint>}
        </DeckTitleGroup>
        {isDirty && <DirtyBadge>{t('editor.unsaved')}</DirtyBadge>}
        <ToolbarActions>
          <Button type="button" $variant="primary" $size="sm" onClick={handleSave}>
            {t('editor.save')}
          </Button>
          <Button type="button" $variant="secondary" $size="sm" onClick={handleResetSlideLayout}>
            {t('editor.resetLayout')}
          </Button>
          <Button type="button" $variant="ghost" $size="sm" onClick={handleResetAll}>
            {t('editor.resetAll')}
          </Button>
          <Button type="button" $variant="ghost" $size="sm" onClick={handleExport}>
            {t('editor.export')}
          </Button>
          <Button type="button" $variant="ghost" $size="sm" onClick={() => fileInputRef.current?.click()}>
            {t('editor.import')}
          </Button>
          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            onChange={handleImportFile}
          />
          <ButtonLink to="/preview" state={{ from: '/edit' }} $variant="secondary" $size="sm">
            {t('editor.preview')}
          </ButtonLink>
        </ToolbarActions>
      </TopBar>

      <EditorBody>
        <EditorGrid>
          <SlideSidebar
            slides={slides}
            selectedIndex={selectedIndex}
            onSelect={selectSlide}
            onAddSlide={handleAddSlide}
            onReorder={handleReorderSlides}
          />

          <ComponentsPanel onAddComponent={handleAddComponent} />

          <CanvasColumn>
            <SlideCanvas
              slide={slide}
              layout={layout}
              logoUrl={logoUrl}
              selectedLayerId={selectedLayerId}
              onSelectLayer={setSelectedLayerId}
              onLayerFrameChange={handleLayerFrameChange}
            />
            <CanvasHint>{t('editor.canvasHint')}</CanvasHint>
          </CanvasColumn>

          <RightColumn>
            <SidebarAccordion>
              <SidebarAccordionSection title={t('editor.accordion.aiEditor')} defaultOpen>
                <AiSlidePanel
                  slides={slides}
                  deckName={deckName}
                  plan={user?.plan}
                  onDeckChange={handleDeckAiChange}
                />
              </SidebarAccordionSection>
              <SidebarAccordionSection title={t('editor.accordion.layers')} defaultOpen>
                <LayersPanel
                  layout={layout}
                  selectedLayerId={selectedLayerId}
                  onSelectLayer={setSelectedLayerId}
                  onLayerOrderChange={handleLayerOrderChange}
                />
              </SidebarAccordionSection>
              <SidebarAccordionSection
                title={
                  selectedLayerId
                    ? t('editor.accordion.editLayer', {
                        label: layerLabel(layout, selectedLayerId),
                      })
                    : t('editor.accordion.selectedElement')
                }
                defaultOpen
              >
                <ElementInspector
                  selectedLayerId={selectedLayerId}
                  slide={slide}
                  layout={layout}
                  logoUrl={logoUrl}
                  onLogoChange={handleLogoChange}
                  onSlideChange={updateSlide}
                  onBackgroundChange={handleBackgroundChange}
                  onImageStyleChange={handleImageStyleChange}
                  onComponentChange={handleComponentChange}
                  onComponentRemove={handleComponentRemove}
                />
              </SidebarAccordionSection>
            </SidebarAccordion>
          </RightColumn>
        </EditorGrid>
      </EditorBody>
    </Page>
  );
};

export default EditorPage;
